import type { I18nArg, I18nArgs } from "./types";

/** "21:30" → « 21 h 30 » (fr) / "9:30 PM" (en); "09:00" → « 9 h » / "9:00 AM". */
export function formatTime(hhmm: string, locale: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  if (locale.startsWith("fr"))
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** Weekday name of a calendar date ("2026-10-20" → « mardi » / "Tuesday"). */
export function formatWeekday(ymd: string, locale: string): string {
  return new Intl.DateTimeFormat(locale.startsWith("fr") ? "fr-CA" : "en-CA", {
    weekday: "long",
    timeZone: "UTC",
  }).format(new Date(`${ymd}T12:00:00Z`));
}

/** 249 → « 249 $ » (fr) / "$249" (en); cents only when the amount has some (0.37 → « 0,37 $ »). */
export function formatMoney(value: number, locale: string): string {
  const cents = !Number.isInteger(value);
  return new Intl.NumberFormat(locale.startsWith("fr") ? "fr-CA" : "en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(value);
}

type Translate = (key: string) => string;

const formatArg = (
  v: I18nArg,
  locale: string,
  translate: Translate,
): string | number => {
  if (typeof v !== "object") return v;
  if ("time" in v) return formatTime(v.time, locale);
  if ("weekday" in v) return formatWeekday(v.weekday, locale);
  if ("text" in v) return v.text;
  if ("money" in v) return formatMoney(v.money, locale);
  return translate(v.label);
};

/** Turns the localized arguments of a quiz text into plain polyglot arguments. */
export function formatArgs(
  args: I18nArgs | undefined,
  locale: string,
  translate: Translate,
): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(args ?? {}).map(([k, v]) => [
      k,
      formatArg(v, locale, translate),
    ]),
  );
}
